import React, { useEffect } from 'react';
import {
  IconAlertTriangle,
  IconCheckCircle,
  IconXCircle,
} from '@pentabd/icons';
import classNames from 'classnames';

import Text from '../../atoms/text/Text';
import { toast as actions } from './toast';
import { defaultTimeouts, updateAction, useStore } from './store';
import { ToastItem, ToastPosition } from './types';
import { useToastItem } from './useToastItem';

const ToastComponent = ({
  toast,
  position,
}: {
  toast: ToastItem;
  position: ToastPosition;
}) => {
  const { handlers } = useToastItem();

  const ref = React.useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        const updateHeight = () => {
          const height = el.getBoundingClientRect().height;
          updateAction(toast.id, height, toast.position || position);
        };
        updateHeight();
        new MutationObserver(updateHeight).observe(el, {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }
    },
    [toast.id],
  );

  const offset = handlers.calculateOffset(toast, {
    defaultPosition: toast.position || position,
  });

  useEffect(() => {
    setTimeout(() => {
      actions.dismiss(toast.id);
    }, defaultTimeouts[toast.type]);
  }, []);

  let direction;

  if (toast.position) {
    direction = toast.position.includes('top') ? 1 : -1;
  } else {
    direction = position.includes('top') ? 1 : -1;
  }

  return (
    <div
      ref={ref}
      key={toast.id}
      className={classNames(
        'toast d-flex justify-content-end position-absolute ',
        `toast-${toast.position}`,
      )}
      style={{
        transform: `translateY(${direction * offset}px)`,
      }}
    >
      <div
        className={classNames(
          'd-flex position-relative  py-10 ps-6 pe-12 rounded shadow-2xl toast-item',
          `bg-${toast.type}-lightest border border-${toast.type}-midlight`,
        )}
      >
        <div className="d-flex gap-4 align-items-center ">
          <div className="d-flex align-items-center shrink-0">
            {toast.icon ? (
              <div
                className={`toast-icon border border border-${toast.type}-midlight rounded d-flex justify-content-center align-items-center`}
              >
                {toast.icon}
              </div>
            ) : null}

            {toast.type === 'success' ? (
              <IconCheckCircle size={24} fill="success" className="shrink-0" />
            ) : null}

            {toast.type === 'danger' ? (
              <IconXCircle size={24} fill="danger" />
            ) : null}

            {toast.type === 'warning' ? (
              <IconAlertTriangle size={24} fill="warning" />
            ) : null}
          </div>

          <Text weight="normal" size="sm" color={toast.type}>
            {toast.message}
          </Text>
        </div>
        <div
          className={classNames('progressBar', `bg-${toast.type}`)}
          style={{
            animation: `progressBar ${
              defaultTimeouts[toast.type] / 1000
            }s ease-in-out forwards`,
          }}
        />
      </div>
    </div>
  );
};

const Toast = ({ position = 'top-right' }: { position?: ToastPosition }) => {
  const { toasts } = useStore();

  if (!toasts.length) {
    return null;
  }

  return (
    <div
      className="position-fixed"
      style={{ inset: '16px', pointerEvents: 'none' }}
    >
      {toasts?.map((toast) => (
        <ToastComponent position={position} key={toast.id} toast={toast} />
      ))}
    </div>
  );
};
export default Toast;
